<template>
  <div class="form-time-entry text-center">
    <TopMenu
      :title="timeEntryTitle"
      back-to="/dashboard/time-entries"
      dark-background/>
    <div class="container-fluid content">
      <form class="row">
        <div
          class="col-12">
          <FormSelect
            :disabled="localTimeEntry.isSubmitted"
            :name="$t('TIME_ENTRIES.EMPLOYER')"
            v-model="localTimeEntry.employer.id"
            :placeholder="$t('TIME_ENTRIES.EMPLOYER')"
            :options="formatToOptions(selectedMemberEmployers, 'companyName', 'id')"
            :validate="localTimeEntry.hasNotWorked ? null : 'required'"
            :required="!localTimeEntry.hasNotWorked"
            class="row-item-transparent-with-border"
            number/>
        </div>
        <div
          v-if="!localTimeEntry.hasNotWorked"
          class="col-12">
          <FormSelect
            :disabled="localTimeEntry.isSubmitted"
            :name="$t('TIME_ENTRIES.ACTIVITY_AREA')"
            v-model="localTimeEntry.activityArea.id"
            :placeholder="$t('TIME_ENTRIES.ACTIVITY_AREA')"
            :options="selectedEmployerActivityAreas"
            class="row-item-transparent-with-border"
            validate="required"
            number
            required/>
        </div>
        <div
          class="col-12">
          <DatePicker
            v-validate="'required'"
            :prepend="$t('TIME_ENTRIES.DATE')"
            :name="$t('TIME_ENTRIES.DATE')"
            :disabled="localTimeEntry.isSubmitted"
            v-model="localTimeEntry.date"
            class="row-item-transparent-with-border"/>
        </div>
        <div
          class="col-12">
          <FormCheckbox
            :disabled="localTimeEntry.isSubmitted"
            v-model="localTimeEntry.hasNotWorked"
            :name="$t('TIME_ENTRIES.I_HAVE_NOT_WORKED')"
            :prepend="$t('TIME_ENTRIES.I_HAVE_NOT_WORKED')"
            class="row-item-transparent-with-border" />
        </div>
        <div
          v-if="!localTimeEntry.hasNotWorked"
          class="col-12">
          <FormTimeInput
            v-validate="'required|time'"
            :disabled="localTimeEntry.isSubmitted"
            :prepend="$t('TIME_ENTRIES.REGULAR_TIME_LABEL')"
            :name="$t('TIME_ENTRIES.REGULAR_TIME_LABEL')"
            v-model="localTimeEntry.regularTime"
            class="row-item-transparent-with-border"
            align="right"
            required/>
        </div>
        <div
          v-if="!localTimeEntry.hasNotWorked"
          class="col-12">
          <FormTimeInput
            v-validate="'time'"
            :disabled="localTimeEntry.isSubmitted"
            :prepend="$t('TIME_ENTRIES.OVER_TIME_LABEL')"
            :name="$t('TIME_ENTRIES.OVER_TIME_LABEL')"
            v-model="localTimeEntry.overTime"
            class="row-item-transparent-with-border"
            align="right"
            required/>
        </div>
        <div class="col-12">
          <ActionButton
            v-if="isAdd && !localTimeEntry.isSubmitted"
            class="btn btn-primary btn-block mt-1 mb-4 pb-1"
            @click="add">{{ $t("TIME_ENTRIES.ADD_TIME_ENTRY_BUTTON") }}</ActionButton>
        </div>
        <div class="col-12">
          <ActionButton
            v-if="!isAdd && !localTimeEntry.isSubmitted"
            class="btn btn-primary btn-block mt-1 mb-1 pb-1"
            @click="save">{{ $t("TIME_ENTRIES.SAVE_BUTTON") }}</ActionButton>
        </div>
        <div class="col-12">
          <ActionButton
            class="btn btn-primary btn-block mt-1 mb-1 pb-1"
            @click="goToSubmit">{{ $t("TIME_ENTRIES.SUBMIT_ENTRIES") }}</ActionButton>
        </div>
        <div
          v-if="!localTimeEntry.hasNotWorked"
          class="col-12">
          <h4 class="mt-3 mb-2">{{ $t('TIME_ENTRIES.EXPENSE_LABEL') }}</h4>
          <div
            v-if="!isAdd"
            :class="{'mb-3': localTimeEntry.isSubmitted}">
            <TextSelector
              v-for="(expense, index) in localTimeEntry.expenses"
              :key="index"
              :border-bottom="index != localTimeEntry.expenses.length -1"
              :id="expense.id"
              class="non-selectable-text"
              @click="goToExpense">
              {{
                $t(expense.expenseType.name) + ' - ' + (expense.expenseType.unit == "$" ? formatMoney(expense.qty) : expense.qty) + expense.expenseType.unit
              }}
            </TextSelector>
            <ActionButton
              v-if="!localTimeEntry.isSubmitted"
              class="btn btn-primary btn-block mt-3 pb-1"
              style="margin-bottom: 1rem !important"
              @click="goToExpense('add')">{{ $t("TIME_ENTRIES.ADD_EXPENSE_BUTTON") }}</ActionButton>
          </div>
          <p v-if="isAdd && !localTimeEntry.isSubmitted">{{ $t("TIME_ENTRIES.SAVE_BEFORE_ADD_EXPENSE") }}</p>
          <p v-if="localTimeEntry.isSubmitted && (!localTimeEntry.expenses || localTimeEntry.expenses.length === 0)">{{ $t("TIME_ENTRIES.EXPENSE.NO_EXPENSE") }}</p>
        </div>

        <div
          v-if="!localTimeEntry.hasNotWorked"
          class="photos-section col-12">
          <h4 class="mt-3 mb-3">{{ $t('TIME_ENTRIES.PHOTO_REPORT_LABEL') }}</h4>
          <div v-if="!isAdd">
            <div class="row">
              <div
                v-for="(attachedFile, index) in localTimeEntry.attachedFiles"
                :key="index"
                class="col-4 mb-3 text-white">
                <PhotoContainer
                  :src="getAttachedFileURL(attachedFile.id)"
                  :show="selectedAttachedFiles?true:false"
                  @click="goToPhoto(attachedFile.id)" />
              </div>
            </div>
            <ActionButton
              v-if="!localTimeEntry.isSubmitted"
              class="btn btn-primary btn-block mt-3 mb-4 pb-1"
              @click="goToPhoto('add')">{{ $t("TIME_ENTRIES.ADD_PHOTO_BUTTON") }}</ActionButton>
          </div>
          <p
            v-if="isAdd"
            class="secondaryColor">{{ $t("TIME_ENTRIES.SAVE_BEFORE_ADD_FILES") }}</p>
          <p
            v-if="localTimeEntry.isSubmitted && (!localTimeEntry.attachedFiles || localTimeEntry.attachedFiles.length === 0)"
            class="secondaryColor">{{ $t("PHOTO.NO_PHOTO") }}</p>
        </div>

        <div class="col-12">
          <h4 class="mt-3 mb-2">{{ $t('TIME_ENTRIES.NOTES_LABEL') }}</h4>
          <FormTextArea
            :disabled="localTimeEntry.isSubmitted"
            v-model="localTimeEntry.note"
            :placeholder="$t('TIME_ENTRIES.NOTE_LABEL')"/>
        </div>
        <div class="col-12">
          <ActionButton
            v-if="!isAdd && !localTimeEntry.isSubmitted"
            class="btn btn-danger btn-block mt-1 mb-4 pb-1"
            @click="showConfirmDelete = true">{{ $t("TIME_ENTRIES.DELETE_TIME_ENTRY_BUTTON") }}</ActionButton>
        </div>
      </form>
    </div>
    <BottomMenu />
    <ConfirmModal
      v-if="showConfirmDelete"
      show-modal
      size="md"
      @cancel="showConfirmDelete = false"
      @confirm="confirmDelete">{{ $t("TIME_ENTRIES.CONFIRM_MODAL_DELETE_TIME_ENTRY") }}</ConfirmModal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import ActionButton from '@/components/Actions/ActionButton';
import FormSelect from '@/components/Forms/FormSelect';
import DatePicker from '@/components/Date/DatePicker';
import FormInput from '@/components/Forms/FormInput';
import FormTimeInput from '@/components/Forms/FormTimeInput';
import FormTextArea from '@/components/Forms/FormTextArea';
import TextSelector from '@/components/Utils/TextSelector';
import PhotoContainer from '@/components/Utils/PhotoContainer';
import FormCheckbox from '@/components/Forms/FormCheckbox';
import UrlHelper from '@/helpers/url-helper';

export default {
  name: 'FormTimeEntry',
  components: {
    TopMenu,
    BottomMenu,
    ConfirmModal,
    ActionButton,
    FormSelect,
    DatePicker,
    FormInput,
    FormTimeInput,
    FormTextArea,
    TextSelector,
    PhotoContainer,
    FormCheckbox,
  },
  mixins: [UrlHelper],
  data() {
    return {
      localTimeEntry: {
        employer: {},
        activityArea: {},
        attachedFiles: [],
        expenses: [],
        date: moment().toDate(),
      },
      showConfirmDelete: false,
    };
  },
  computed: {
    ...mapState('employer', ['selectedMemberEmployers']),
    ...mapState('timeEntry', ['selectedTimeEntry']),
    ...mapState('attachedFile', ['selectedAttachedFiles']),
    isAdd() {
      return this.$route.params.id === 'add';
    },
    selectedEmployerActivityAreas() {
      if (!this.localTimeEntry || !this.selectedMemberEmployers) {
        return [];
      }

      const employer = this.selectedMemberEmployers.find(
        emp => emp.id === this.localTimeEntry.employer.id,
      );

      if (employer) {
        return this.formatToOptions(employer.activityAreas, 'name', 'id');
      }

      return [];
    },
    timeEntryTitle() {
      let title = this.$t('TIME_ENTRIES.TIME_ENTRY_LABEL_EDIT');
      if (this.isAdd) {
        title = this.$t('TIME_ENTRIES.TIME_ENTRY_LABEL_ADD');
      } else if (this.localTimeEntry.isSubmitted) {
        title = this.$t('TIME_ENTRIES.TIME_ENTRY_LABEL');
      }
      return title;
    },
  },
  watch: {
    selectedTimeEntry: {
      handler(newVal) {
        if (newVal) {
          this.localTimeEntry = cloneDeep(newVal);
          this.localTimeEntry.regularTime = this.formatTime(
            this.localTimeEntry.regularTime,
          );
          this.localTimeEntry.overTime = this.formatTime(
            this.localTimeEntry.overTime,
          );
          if (this.localTimeEntry.date) {
            this.localTimeEntry.date = moment(
              this.localTimeEntry.date,
            ).toDate();
          } else {
            this.localTimeEntry.date = moment().toDate();
          }
          if (!this.localTimeEntry.employer) {
            this.localTimeEntry.employer = {};
          }
          if (!this.localTimeEntry.activityArea) {
            this.localTimeEntry.activityArea = {};
          }
        } else {
          this.localTimeEntry = {
            date: moment().toDate(),
            employer: {},
            activityArea: {},
            attachedFiles: [],
            expenses: [],
          };
        }
      },
      immediate: true,
    },
    selectedMemberEmployers: {
      handler(newVal) {
        if (newVal && newVal.length === 1) {
          this.localTimeEntry.employer.id = newVal[0].id;
        }
      },
    },
    immediate: true,
  },
  created() {
    this.$store.dispatch('timeEntry/resetTimeEntry');
    this.$store.dispatch('attachedFile/resetAttachedFiles');
    this.$store.dispatch('employer/currentMemberEmployers').then(() => {
      if (this.$route.params.id !== 'add') {
        this.$store.dispatch('timeEntry/timeEntryById', this.$route.params.id).then(() => {
          if (this.localTimeEntry.attachedFiles && this.localTimeEntry.attachedFiles.length > 0) {
            const attachedFileIds = [];
            for (let i = 0; i < this.localTimeEntry.attachedFiles.length; i += 1) {
              attachedFileIds.push(this.localTimeEntry.attachedFiles[i].id);
            }
            this.$store.dispatch('attachedFile/attachedFiles', attachedFileIds);
          }
        });
      }
    });
  },
  methods: {
    formatToOptions(array, textAttribute, valueAttribute) {
      if (!array) {
        return [];
      }
      return array.map(item => ({
        text: this.$t(item[textAttribute]),
        value: parseInt(item[valueAttribute], 10),
      }));
    },
    formatTime(value) {
      let t = '0:00';
      if (value) {
        const values = value.split(':');
        t = `${parseInt(values[0], 10).toString()}:${values[1]}`;
      }
      return t;
    },
    formatMoney(value) {
      return parseFloat(value).toFixed(2);
    },
    goToExpense(id) {
      this.$router.push(
        `/dashboard/time-entries/${this.$route.params.id}/expense/${id}`,
      );
    },
    save() {
      if (!this.localTimeEntry.hasNotWorked) {
        this.$validator.validateAll().then(() => {
          if (this.$validator.errors.items.length > 0) {
            this.$store.commit('alerts/setAlert', {
              msg: this.$t(
                `VALIDATION.${
                  this.$validator.errors.items[0].rule.toUpperCase()}`,
                [this.$validator.errors.items[0].field],
              ),
            });
          } else {
            this.$store.dispatch('timeEntry/saveTimeEntry', this.localTimeEntry);
          }
        });
      } else {
        this.$store.dispatch('timeEntry/saveTimeEntry', this.buildHasNotWorkedEntry());
      }
    },
    add() {
      if (!this.localTimeEntry.hasNotWorked) {
        this.$validator.validateAll().then(() => {
          if (this.$validator.errors.items.length > 0) {
            this.$store.commit('alerts/setAlert', {
              msg: this.$t(
                `VALIDATION.${
                  this.$validator.errors.items[0].rule.toUpperCase()}`,
                [this.$validator.errors.items[0].field],
              ),
            });
          } else {
            this.$store.dispatch('timeEntry/addTimeEntry', this.localTimeEntry);
          }
        });
      } else {
        this.$store.dispatch('timeEntry/addTimeEntry', this.buildHasNotWorkedEntry());
      }
    },
    confirmDelete() {
      this.showConfirmDelete = false;
      this.$store.dispatch('timeEntry/deleteTimeEntry', this.$route.params.id);
    },
    goToPhoto(id) {
      this.$router.push(
        `/dashboard/time-entries/${this.$route.params.id}/photo/${id}`,
      );
    },
    goToSubmit() {
      this.$router.push(
        '/dashboard/time-entries',
      );
    },
    buildHasNotWorkedEntry() {
      const timeEntry = {};
      timeEntry.id = this.localTimeEntry.id;
      if (this.localTimeEntry.employer.id) {
        timeEntry.employer = this.localTimeEntry.employer;
      } else {
        timeEntry.employer = null;
      }
      timeEntry.date = this.localTimeEntry.date;
      timeEntry.hasNotWorked = this.localTimeEntry.hasNotWorked;
      timeEntry.note = this.localTimeEntry.note;
      return timeEntry;
    },
    getAttachedFileURL(id) {
      let attachedFile = {};
      if (this.selectedAttachedFiles) {
        this.selectedAttachedFiles.forEach((att) => {
          if (att.id === id) attachedFile = att;
        });
      }
      return `data:${attachedFile.contentType};base64,${attachedFile.file}`;
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.form-time-entry {
    height: 100%;
  }
  &.content {
    padding-top: $top-menu-height;
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
}
h4 {
  text-align: left;
  margin-bottom: 0px;
  text-transform: uppercase;
  font-weight: bold;
}
.photos-section {
  background-color: $primary;
}
.photos-section h4 {
  color: $base;
}
</style>
